import api from "@/services/api";
import get from "lodash/get"
import head from "lodash/head"

export const hasRetrievedBasicInfo = state => state.hasRetrievedBasicInfo

export function athleteId(state) {
    return state.user.id
}

export function userProfilePhotoUrl(state) {
    if (state.user.images && state.user.images.length) {
        const profileImage = state.user.images.find(image => image.isProfile == true);

        if (profileImage) {
            return profileImage.url;
        }
    }
    return null;
}

export const athlete = state => state.user
export const favoritesReceived = state => state.user.favoritesReceived || []
export const favoritedUserId = (state, getters) => get(head(getters.favoritesReceived), 'athleteId')
export const skips = state => state.user.skips || []
export const skippedUserId = (state, getters) => get(head(getters.skips), 'athleteId')
export const firstName = state => state.user.firstName
export const lastName = state => state.user.lastName
export const urlSlug = state => state.user.urlSlug
export const isSendingFeedbackRequest = state => state.isSendingFeedbackRequest
export const gender = state => state.user.gender
export const videos = state => state.user.videos && state.user.videos.filter(vid => vid.isVisible && !vid.isDraft) || []
export const events = state => state.user.events
export const sport = state => state.user.sport
export const classYear = state => state.user.classYear
export const isTakingEvaluationAction = state => state.isTakingEvaluationAction
export const isFavorited = (state, getters) => !!(getters.favoritesReceived && getters.favoritesReceived.length)
export const isSkipped = (state, getters) => !!(getters.skips && getters.skips.length)
export const teamAffiliations = state => state.user.teamAffiliations || []
export const nonIntegratedAffiliations = state => state.user.nonIntegratedAffiliations || []
export const actionTimestamp = state => state.actionTimestamp
export const isVerified = state => state.user.isVerified

export function organizationAffiliations({ user }) {
    return [
        ...user.clubAffiliations || [],
        ...user.highSchoolAffiliations || [],
    ]
}

export function isSameAthlete(state, getters, rootState, rootGetters) {
    return rootGetters['userData/isLoggedIn']
        && rootGetters['userData/userId'] == getters.athleteId
}

export function isApplicableCollegeCoach(state, getters, rootState, rootGetters) {
    return rootGetters['userData/isCollegeCoach']
        && rootGetters['userData/sport'].id == get(getters.sport, 'id')
        && rootGetters['userData/relevantGender'] == getters.gender
}

export function canSendMessage(state) {
    return state.user.canBeMessaged === true
}

export function isApplicableClubStaff(state, getters, rootState, rootGetters) {
    if (!rootGetters['userData/isClubStaff']) {
        return false
    }

    if (!getters.organizations.find(org => org.id == rootGetters['userData/organization'].id)) {
        return false
    }

    if (rootGetters['userData/isAdmin']) {
        return true
    }

    const sharedTeam = rootGetters['userData/teamAffiliations'].find(({ team: staffTeam }) => {
        return getters.teams.find(athleteTeam => athleteTeam.id == staffTeam.id)
    })

    if (sharedTeam) {
        return true
    }

    return false
}

export function canFavorite(state, getters) {
    return getters.isApplicableCollegeCoach
        && getters.hasRetrievedBasicInfo
        && !getters.favoritesReceived.length
}

export function canSkip(state, getters) {
    return getters.isApplicableCollegeCoach
        && getters.hasRetrievedBasicInfo
        && !getters.skips.length
}

export function canSendFeedbackRequest(state, getters) {
    return getters.isApplicableCollegeCoach
        && getters.organizations.length
        && getters.hasRetrievedBasicInfo
}

export function canShare(state, getters) {
    return getters.isSameAthlete
        || getters.isApplicableClubStaff
}

export function isUserWithoutAccess(state, getters, rootState, rootGetters) {
    if (!rootGetters['userData/isLoggedIn']) {
        return true;
    }
    if (getters.isApplicableCollegeCoach) {
        return false;
    }
    return !getters.canShare;
}

export function userFullName(state) {
    return `${state.user.firstName ?? ''} ${state.user.lastName ?? ''}`
}

export function allPositionsString(state) {
    if (state.user.positions && state.user.positions.length) {
        return state.user.positions.join(", ");
    }

    return null;
}

export function homeTownString(state) {
    if (!state.user.city && !state.user.state) {
        return null;
    }
    return [state.user.city, state.user.state]
        .filter(word => word)
        .join(', ')
}

export function genderLetter(state) {
    if (state.user.gender) {
        return state.user.gender.charAt(0).toUpperCase();
    }

    return null;
}

export function organizations({ user }) {
    return [
        ...user.clubAffiliations || [],
        ...user.highSchoolAffiliations || [],
    ].map(affiliation => affiliation.organization)
}

export function teams({ user }) {
    return user.teamAffiliations.map(affiliation => affiliation.team)
}

export function allClubsString(state) {
    if (state.user.clubAffiliations && state.user.clubAffiliations.length) {
        return state.user.clubAffiliations.map(club => club.organization.name).join(", ");
    }

    return null;
}

export function allHighSchoolAffiliations(state) {
    /**
     * Don't show non-integrated highschool if you're on an integrated one.
     */
    const nonIntegrated = !isOnIntegratedHighschool(state) && state.user.nonIntegratedAffiliations ? state.user.nonIntegratedAffiliations.filter(affiliation => affiliation.type == "highSchool") : null;
    const integrated = state.user.highSchoolAffiliations ? state.user.highSchoolAffiliations.map(org => {
        org.isIntegrated = true;
        return org;
    }) : null;

    return [
        ...integrated || [],
        ...nonIntegrated || [],
    ];
}

export function allClubAffiliations(state) {
    /**
     * Don't show non-integrated clubs if the user is on an integrated one.
     */
    const nonIntegrated = !isOnIntegratedClub(state) && state.user.nonIntegratedAffiliations ? state.user.nonIntegratedAffiliations.filter(affiliation => affiliation.type == "club") : null;
    const integrated = state.user.clubAffiliations ? state.user.clubAffiliations.map(org => {
        org.isIntegrated = true;
        return org;
    }) : null;

    return [
        ...integrated || [],
        ...nonIntegrated || [],
    ];
}

export function isCurrentUser(state) {
    return api.clientData('user_id') == state.user.id;
}

export function isCommitted(state) {
    return state.user.commitment != null;
}

export function isPreCommitted(state) {
    return state.user.commitment != null && state.user.commitment.preCommitment && !state.user.commitment.createdDate;
}

export function commitmentSchoolName(state) {
    if (!state.user.commitment || !state.user.commitment.program || !state.user.commitment.program.college) {
        return null;
    }

    return state.user.commitment.program.college.name;
}

export function commitmentSchoolLocation(state) {
    if (!state.user.commitment || !state.user.commitment.program || !state.user.commitment.program.college) {
        return null;
    }

    return [state.user.commitment.program.college.city, state.user.commitment.program.college.state]
        .filter(word => word)
        .join(', ')
}

export function commitmentSchoolDivision(state) {
    if (!state.user.commitment || !state.user.commitment.program || !state.user.commitment.program.division) {
        return null;
    }

    return state.user.commitment.program.division.name;
}

export function commitmentSchoolConference(state) {
    if (!state.user.commitment || !state.user.commitment.program || !state.user.commitment.program.conference) {
        return null;
    }

    return state.user.commitment.program.conference.name;

}

export function commitmentSchoolUrl(state) {
    if (!state.user.commitment || !state.user.commitment.program) {
        return null;
    }

    return state.user.commitment.program.url;
}

export function commitmentSchoolImage(state) {
    if (!state.user.commitment || !state.user.commitment.program || !state.user.commitment.program.college || !state.user.commitment.program.college.images) {
        return null;
    }

    const profileImage = state.user.commitment.program.college.images.find(image => image.isProfile == true);

    if (profileImage) {
        return profileImage.url;
    }
    return null;
}

export function commitmentDate(state) {
    if (!state.user.commitment || !state.user.commitment.createdDate) {
        return null;
    }

    return new Date(state.user.commitment.createdDate).toLocaleDateString("en-US");

}

export function athleteIsInterestedInProgram(state) {

    if (!state.user.interests) {
        return null;
    }

    const current_program_id = api.clientData('user_program_id');

    return state.user.interests.find(interest => {
        return interest.program.id == current_program_id;
    });
}

export function highSchoolString(state) {
    if (state.user.highSchoolAffiliations && state.user.highSchoolAffiliations.length) {
        return state.user.highSchoolAffiliations[0].organization.name
    }

    if (!state.user.nonIntegratedAffiliations || !state.user.nonIntegratedAffiliations.length) {
        return null;
    }

    const highSchoolNonIntegrated = state.user.nonIntegratedAffiliations.find(affiliation => affiliation.type == "highSchool");

    if (highSchoolNonIntegrated) {
        return highSchoolNonIntegrated.team.organization.name + (highSchoolNonIntegrated.team.organization.state ? `, ${highSchoolNonIntegrated.team.organization.state}` : "");
    }

    return null;
}

export function sportName(state) {
    if (state.user.sport && state.user.sport.name) {
        return (
            state.user.sport.name.charAt(0).toUpperCase() +
            state.user.sport.name.slice(1)
        );
    }
    return null;
}

export function sportHeroImage(state) {
    if (state.user.sport && state.user.sport.name) {
        switch (state.user.sport.name) {
            case "lacrosse":
                return "https://cdn.sportsrecruits.com/assets/Athlete-profile-assets/lacrosse.png";
            case "volleyball":
                return "https://cdn.sportsrecruits.com/assets/Athlete-profile-assets/volleyball.png";
            case "field hockey":
                return "https://cdn.sportsrecruits.com/assets/Athlete-profile-assets/field-hockey.png";
            case "soccer":
                return "https://cdn.sportsrecruits.com/assets/Athlete-profile-assets/soccer.png";
            case "softball":
                return "https://cdn.sportsrecruits.com/assets/Athlete-profile-assets/softball.png";
            case "baseball":
                return "https://cdn.sportsrecruits.com/assets/Athlete-profile-assets/baseball.png";
            case "swimming and diving":
                return "https://cdn.sportsrecruits.com/assets/Athlete-profile-assets/swimming.png";
            case "basketball":
                return "https://cdn.sportsrecruits.com/assets/Athlete-profile-assets/basketball.png";
            case "ice hockey":
                return "https://cdn.sportsrecruits.com/assets/Athlete-profile-assets/ice-hockey.png";
            case "rowing":
                return "https://cdn.sportsrecruits.com/assets/Athlete-profile-assets/rowing.png";
            case "gymnastics":
                return "https://cdn.sportsrecruits.com/assets/Athlete-profile-assets/gymnastics.png";
            case "water polo":
                return "https://cdn.sportsrecruits.com/assets/Athlete-profile-assets/water-polo.png";
            case "wrestling":
                return "https://cdn.sportsrecruits.com/assets/Athlete-profile-assets/wrestling.png";
            case "ping pong":
                return "https://cdn.sportsrecruits.com/assets/Athlete-profile-assets/pong.png";
            case "tennis":
                return "https://cdn.sportsrecruits.com/assets/Athlete-profile-assets/tennis.png";
            case "squash":
                return "https://cdn.sportsrecruits.com/assets/Athlete-profile-assets/squash.png";
            case "football":
                return "https://cdn.sportsrecruits.com/assets/Athlete-profile-assets/football.png";
            case "beach volleyball":
                return "https://cdn.sportsrecruits.com/assets/Athlete-profile-assets/beach-volleyball.png";
            case "golf":
                return "https://cdn.sportsrecruits.com/assets/Athlete-profile-assets/golf.png";
            case "skiing":
                return "https://cdn.sportsrecruits.com/assets/Athlete-profile-assets/skiing.png";
            case "track and field":
                return "https://cdn.sportsrecruits.com/assets/Athlete-profile-assets/track-field.png";
            case "cross country":
                return "https://cdn.sportsrecruits.com/assets/Athlete-profile-assets/cross-country.png";
            case "esports":
                return "https://cdn.sportsrecruits.com/assets/Athlete-profile-assets/esports.png";

        }
    }
    return null;
}

export function isOnIntegratedTeam(state) {
    return state.user.teamAffiliations != null ? state.user.teamAffiliations.length > 0 : false;
}

export function isOnIntegratedHighschool(state) {
    return state.user.highSchoolAffiliations ? state.user.highSchoolAffiliations.length > 0 : false;
}

export function isOnIntegratedClub(state) {
    return state.user.clubAffiliations ? state.user.clubAffiliations.length > 0 : false;
}

export function satScore(state) {
    if (state.user.satScores == null || state.user.satScores.verbal == null || state.user.satScores.math == null) {
        return null;
    }

    return {
        score: parseInt(state.user.satScores.verbal) + parseInt(state.user.satScores.math),
        scale: 1600,
        verbalScore: parseInt(state.user.satScores.verbal),
        mathScore: parseInt(state.user.satScores.math),
    }
}

export function actScore(state) {
    if (state.user.actScore == null) {
        return null;
    }

    return {
        score: parseInt(state.user.actScore),
        scale: 36,
    };
}

export function sat2Scores(state) {
    if (state.user.sat2Scores == null || state.user.sat2Scores.length <= 0) {
        return []
    }

    return state.user.sat2Scores.map(score => {
        return {
            score: parseInt(score.score),
            scale: 800,
            subject: score.subject,
        };
    })
}

export function psatScore(state) {
    if (state.user.psatScores == null || state.user.psatScores.verbal == null || state.user.psatScores.math == null) {
        return null;
    }
    function checkMinMax(score) {
        score = parseInt(score);
        if (score < 160) return 160
        else if (score > 760) return 760
        else return score
    }
    return {
        score: checkMinMax(state.user.psatScores.verbal) + checkMinMax(state.user.psatScores.math),
        scale: 1520,
        verbalScore: checkMinMax(state.user.psatScores.verbal),
        mathScore: checkMinMax(state.user.psatScores.math),
    }
}

export function hasTestScores(state) {
    return satScore(state) != null || actScore(state) != null || sat2Scores(state).length > 0 || psatScore(state) != null;
}

export function hasGPA(state) {
    return state.user.gpa !== null && state.user.gpa.value !== null && state.user.gpa.value > 0;
}

export function hasAcademicInfo(state, getters) {
    return state.user.classYear != null
        || ((
                getters.hasGPA
                || state.user.classRank != null
                || state.user.ncaaEligibilityNumber != null
                || state.user.intendedMajor != null
                || state.user.academicHonors != null
                || state.user.extracurricularActivities != null
                || getters.hasTestScores
            )
        && !getters.isUserWithoutAccess);
}

export const similarUncommittedAthletesCount = state => state.user.similarUncommittedAthletesCount ?? 0

export const skills = state => state.skills;

export const user = state => state.user;

export const siteId = state => state.user.sport?.id;

export const videoPlaylists = state => (state.videoPlaylists || []).filter(playlist => !playlist.isPinned);

export const shownPlaylists = (state, getters) => (initialPageSize = 4) => getters.videoPlaylists.slice(0, initialPageSize);

export const videoPlaylistPlaying = state => state.videoPlaylistPlaying;

export const shareLinkVideo = state => state.shareLinkVideo;